
import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Download, Database, PlusCircle, Filter, FileEdit, AlertTriangle, Gauge } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Bike } from '@/services/types';

interface BikeActionsProps {
  fetchBikes: () => void;
  dataSource: 'supabase' | 'mock';
  setStatusFilter: (status: string | null) => void;
  setMovementFilter: (filter: string | null) => void;
  bikes: Bike[];
  setShowAddDialog: (show: boolean) => void;
}

export const BikeActions: React.FC<BikeActionsProps> = ({ 
  fetchBikes, 
  dataSource, 
  setStatusFilter,
  setMovementFilter,
  bikes,
  setShowAddDialog
}) => {
  const handleExportCSV = () => {
    try {
      const csvContent = [
        // Заголовки
        ['ID', 'Серийный номер', 'Модель', 'GPS-метка', 'Статус', 'Состояние движения', 'Последнее ТО'].join(','),
        // Данные
        ...bikes.map(bike => [
          bike.id,
          bike.serialNumber,
          bike.model,
          bike.gps_tag || 'Не задана',
          bike.status === 'available' ? 'Доступен' : bike.status === 'in_use' ? 'Используется' : 'Ремонт',
          bike.movement_blocked ? 'Заблокирован' : bike.speed_limit ? `Ограничен ${bike.speed_limit} км/ч` : 'Нормальный',
          new Date(bike.lastMaintenance).toLocaleDateString()
        ].join(','))
      ].join('\n');
      
      // Создаем Blob для загрузки
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      // Создаем ссылку для загрузки
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Велосипеды_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Экспорт выполнен успешно');
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      toast.error('Не удалось экспортировать данные');
    }
  };

  return (
    <div className="flex space-x-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchBikes}
              className={`flex items-center gap-2 ${dataSource === 'mock' ? 'text-amber-600 border-amber-300' : ''}`}
            >
              <RefreshCw className="h-4 w-4" />
              {dataSource === 'supabase' ? 'Обновить данные' : 'Повторить подключение'}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Источник данных: {dataSource === 'supabase' ? 'Supabase' : 'Тестовые данные'}</p>
            {dataSource === 'mock' && (
              <p className="text-xs mt-1">Таблица bikes пуста или недоступна</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {dataSource === 'mock' && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => window.open('https://supabase.com/dashboard/project/urwzhudyitjovstzqfdj/editor', '_blank')}
          className="text-amber-600 border-amber-300"
        >
          <Database className="mr-2 h-4 w-4" />
          SQL редактор
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="mr-2 h-4 w-4" />
            Статус
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => setStatusFilter(null)}>
              Все статусы
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setStatusFilter('available')}>
              Доступные
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setStatusFilter('in_use')}>
              Используемые
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setStatusFilter('maintenance')}>
              На ремонте
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Gauge className="mr-2 h-4 w-4" />
            Движение
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => setMovementFilter(null)}>
              Все состояния
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setMovementFilter('blocked')} className="text-red-600">
              <AlertTriangle className="mr-2 h-4 w-4" />
              Заблокированные
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setMovementFilter('limited')} className="text-yellow-600">
              <Gauge className="mr-2 h-4 w-4" />
              С ограничением скорости
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setMovementFilter('normal')} className="text-green-600">
              Нормальные
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleExportCSV}
      >
        <Download className="mr-2 h-4 w-4" />
        Экспорт
      </Button>

      <Button 
        size="sm"
        onClick={() => setShowAddDialog(true)}
      >
        <PlusCircle className="mr-2 h-4 w-4" />
        Добавить велосипед
      </Button>
    </div>
  );
};
